import React, { useEffect, useState } from "react";
import { useAuth } from "../../../context/Auth";
import { RotatingLines } from "react-loader-spinner";
import { toast } from "react-toastify";
import ExportData from "../../../components/common/ExportData";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  FileText,
  Calendar,
  Building2,
  ArrowRightLeft,
  Package,
  TrendingUp,
  Eye,
} from "lucide-react";

const InterbranchReport = () => {
  const { requestWithAuth } = useAuth();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRange, setDateRange] = useState("Last 30 Days");
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("");
  const [branchName, setBranchName] = useState("");
  const [transferType, setTransferType] = useState("");
  const [transferList, setTransferList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const getDateRange = (value) => {
    let start_date = "";
    let end_date = "";

    if (value === "Today") {
      start_date = formatDate(new Date());
      end_date = formatDate(new Date());
    } else if (value === "Yesterday") {
      start_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 1))
      );
      end_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 1))
      );
    } else if (value === "Last 7 Days") {
      start_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 7))
      );
      end_date = formatDate(new Date());
    } else if (value === "Last 30 Days") {
      start_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 30))
      );
      end_date = formatDate(new Date());
    } else if (value === "This Month") {
      start_date = formatDate(new Date(new Date().setDate(1)));
      end_date = formatDate(new Date());
    } else if (value === "Last Month") {
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      start_date = formatDate(
        new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1)
      );
      end_date = formatDate(
        new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0)
      );
    }

    setStartDate(start_date);
    setEndDate(end_date);
    setDateRange(value);
  };

  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?pageSize=100`,
        "GET"
      );
      setBranches(data?.results);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchTransferList = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/report/interbranch?page=${current_page}&pageSize=${page_size}&search=${search_query}&branch=${branch}&startDate=${startDate}&endDate=${endDate}&transferType=${transferType}`,
        "GET"
      );
      setTransferList(data?.results);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / pageSize));
    } catch (error) {
      console.error("Error fetching list:", error);
      setTransferList([]);
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async () => {
    try {
      const data = await requestWithAuth(
        `/report/interbranch?page=1&pageSize=10000000000000000000000&search=${search}&branch=${branch}&startDate=${startDate}&endDate=${endDate}&transferType=${transferType}`,
        "GET"
      );
      setTableData(data?.results);
    } catch (error) {
      console.error("Error fetching table data:", error);
      setTableData([]);
    }
  };

  const applyFilters = () => {
    if (branch === "") {
      return toast.error("Please select a branch");
    }

    setCurrentPage(1);
    setShowResults(true);
    fetchTransferList(pageSize, search, 1);
    getTableData();
  };

  const clearFilters = () => {
    setBranch("");
    setBranchName("");
    setTransferType("");
    setSearch("");
    getDateRange("Last 30 Days");
    setCurrentPage(1);
    setShowResults(false);
    setTransferList([]);
  };

  useEffect(() => {
    fetchBranches();
    getDateRange("Last 30 Days");
  }, []);

  useEffect(() => {
    if (showResults && startDate && endDate && branch && transferType) {
      fetchTransferList(pageSize, search, currentPage);
      getTableData();
    }
  }, [currentPage, pageSize]);

  const filterTransferList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Pending: "bg-yellow-100 text-yellow-800",
      Approved: "bg-green-100 text-green-800",
      Rejected: "bg-red-100 text-red-800",
    };
    return (
      <span
        className={`px-3 py-1 rounded-lg text-xs font-semibold ${statusConfig[status] || "bg-gray-100 text-gray-800"
          }`}
      >
        {status}
      </span>
    );
  };

  const calculateTotals = () => {
    const totalQuantity = transferList?.reduce(
      (sum, item) => sum + (Number(item?.quantity) || 0),
      0
    );
    const totalCost = transferList?.reduce(
      (sum, item) => sum + (Number(item?.total_cost) || 0),
      0
    );
    const totalItems = transferList?.reduce(
      (sum, item) => sum + (Number(item?.total_items) || 0),
      0
    );
    return { totalQuantity, totalCost, totalItems };
  };

  const { totalQuantity, totalCost, totalItems } = calculateTotals();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Reports</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Interbranch Report
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <ArrowRightLeft size={32} className="text-blue-600" />
                Interbranch Transfer Report
              </h1>
            </div>
          </div>
        </div>

        {/* Summary Cards - Only show when results are visible */}
        {showResults && transferList?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Package className="text-green-600" size={20} />
                </div>
                <h3 className="text-sm font-medium text-gray-600">
                  Total Quantity
                </h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {totalQuantity}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="text-blue-600" size={20} />
                </div>
                <h3 className="text-sm font-medium text-gray-600">
                  Total Cost
                </h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                GHS {parseFloat(totalCost || 0).toFixed(2)}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileText className="text-purple-600" size={20} />
                </div>
                <h3 className="text-sm font-medium text-gray-600">
                  Total Items
                </h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">{totalItems}</p>
            </div>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Filters Section */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Filter Transfer Report
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Branch Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <Building2 size={12} className="inline mr-1" />
                  Branch
                </label>
                <select
                  value={branch}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "Consolidated") {
                      setBranch("Consolidated");
                      setBranchName("Consolidated");
                    } else if (value === "") {
                      setBranch("");
                      setBranchName("");
                    } else {
                      const id = Number(value);
                      const selectedBranch = branches.find((b) => b.id === id);
                      setBranch(id);
                      setBranchName(selectedBranch?.name || "");
                    }
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="">Select Branch</option>
                  <option value="Consolidated">Consolidated</option>
                  {branches?.map((branchItem, index) => (
                    <React.Fragment key={index}>
                      {!branchItem?.is_head_office && (
                        <option value={branchItem.id}>{branchItem.name}</option>
                      )}
                    </React.Fragment>
                  ))}
                </select>
              </div>

              {/* Date Range Preset */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <Calendar size={12} className="inline mr-1" />
                  Date Range
                </label>
                <select
                  value={dateRange}
                  onChange={(e) => getDateRange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="Today">Today</option>
                  <option value="Yesterday">Yesterday</option>
                  <option value="Last 7 Days">Last 7 Days</option>
                  <option value="Last 30 Days">Last 30 Days</option>
                  <option value="This Month">This Month</option>
                  <option value="Last Month">Last Month</option>
                  <option value="Custom">Custom Range</option>
                </select>
              </div>
            </div>

            {/* Custom Date Range */}
            {dateRange === "Custom" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  />
                </div>
              </div>
            )}

            {/* Filter Actions */}
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Clear Filters
              </button>
              <button
                onClick={applyFilters}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <RotatingLines
                      visible={true}
                      height="16"
                      width="16"
                      strokeColor="white"
                      strokeWidth="5"
                      animationDuration="0.75"
                    />
                    Loading...
                  </>
                ) : (
                  "Generate Report"
                )}
              </button>
            </div>
          </div>

          {/* Results Section - Only show after applying filters */}
          {showResults && (
            <>
              {/* Controls Bar */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <ExportData
                      tableData={tableData}
                      fileName="Interbranch Report"
                    />
                    <span className="text-sm text-gray-600">Show</span>
                    <select
                      onChange={(e) => setPageSize(e.target.value)}
                      value={pageSize}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                    <span className="text-sm text-gray-600">entries</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">Filter:</span>
                    <div className="relative">
                      <Search
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        size={16}
                      />
                      <input
                        type="text"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => filterTransferList(e.target.value)}
                        className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="flex items-center gap-3 text-gray-600">
                      <RotatingLines
                        visible={true}
                        height="24"
                        width="24"
                        strokeColor="#3B82F6"
                        strokeWidth="5"
                        animationDuration="0.75"
                      />
                      <span>Loading transfers...</span>
                    </div>
                  </div>
                ) : transferList?.length > 0 ? (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Source Branch
                        </th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Drug
                        </th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Total Cost
                        </th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Recorded By
                        </th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Approved By
                        </th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Posted Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {transferList?.map((item, index) => (
                        <tr
                          key={index}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="py-4 px-6 text-sm font-medium text-gray-900">
                            {item?.branch}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {item?.drug}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {item?.quantity}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            GHS {parseFloat(item?.total_cost || 0).toFixed(2)}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {item?.recorded_by}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {item?.approved_by}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {item?.status}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {item?.created_at}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="py-20 text-center">
                    <div className="text-gray-400 mb-2">
                      <FileText
                        className="mx-auto h-12 w-12"
                        strokeWidth={1.5}
                      />
                    </div>
                    <p className="text-gray-500 text-sm">
                      No transfer data found
                    </p>
                    <p className="text-gray-400 text-xs mt-1">
                      Try adjusting your filters
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {transferList?.length > 0 && (
                <div className="p-6 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      Showing page {currentPage} of {totalPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-lg transition-all ${currentPage > 1
                          ? "text-gray-600 hover:bg-gray-100"
                          : "text-gray-300 cursor-not-allowed"
                          }`}
                      >
                        <ChevronLeft size={20} />
                      </button>

                      <div className="flex items-center gap-1">
                        {renderPageNumbers()}
                      </div>

                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                          ? "text-gray-600 hover:bg-gray-100"
                          : "text-gray-300 cursor-not-allowed"
                          }`}
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Empty State - Show before applying filters */}
          {!showResults && (
            <div className="py-20 text-center">
              <div className="text-gray-400 mb-3">
                <ArrowRightLeft
                  className="mx-auto h-16 w-16"
                  strokeWidth={1.5}
                />
              </div>
              <p className="text-gray-600 text-lg font-medium mb-1">
                Select Filters to Generate Report
              </p>
              <p className="text-gray-400 text-sm">
                Choose a branch, transfer type, and date range above to view
                transfer data
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterbranchReport;
